<template>
  <div class="dashboard-page dashboard-page--compact">
    <page-loading
      v-if="loading && !hasLoadedOnce"
      message="Carregando dashboard"
      subtitle="Calculando indicadores do período"
      :preview-count="4"
    />
    <template v-else>
    <div class="header header--compact">
      <div class="header__titles">
        <title-vue title="Dashboard" />
        <div class="header__welcome-row">
          <p class="dashboard-subtitle mb-0">{{ welcomeSubtitle }}</p>
          <premium-member-badge
            v-if="isAssociatePremiumActive"
            class="ml-3 flex-shrink-0"
            size="x-small"
          />
        </div>
        <p v-if="selectedFranchiseLabel" class="dashboard-context">
          <v-icon small class="dashboard-context__icon">mdi-store-outline</v-icon>
          {{ selectedFranchiseLabel }}
        </p>
      </div>
      <div class="header__actions">
        <div class="header-period">
          <div class="field-label-with-help">
            Período
            <v-tooltip bottom max-width="280">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  small
                  class="help-icon"
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-help-circle-outline
                </v-icon>
              </template>
              Define o intervalo de datas usado para calcular os indicadores.
            </v-tooltip>
          </div>
          <v-dialog ref="dialog" v-model="dialog" persistent width="290px">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="handleFormatDate"
                class="header-period__field"
                label="Selecione o período"
                prepend-inner-icon="mdi-calendar-range"
                readonly
                dense
                filled
                hide-details
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker v-model="selectedRange" range locale="pt-br">
              <v-btn @click="closeDialog" color="primary" text>Cancelar</v-btn>
              <v-btn @click="handleLoadDashboard" color="primary">Aplicar</v-btn>
            </v-date-picker>
          </v-dialog>
        </div>
        <div class="header__status">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                class="mr-1"
                :loading="inlineRefreshing"
                :disabled="loading && !hasLoadedOnce"
                aria-label="Atualizar indicadores"
                v-bind="attrs"
                v-on="on"
                @click="refreshDashboard"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </template>
            Atualizar dados mantendo filtros atuais
          </v-tooltip>
          <v-chip color="#EEF3FF" text-color="#24305E" class="font-weight-medium update-chip">
            <v-icon left small>mdi-calendar-clock</v-icon>
            Atualizado: {{ lastUpdatedLabel }}
          </v-chip>
        </div>
      </div>
    </div>

    <v-card
      v-if="isAssociatePremiumActive"
      outlined
      class="premium-member-card mb-4"
    >
      <v-card-text class="premium-member-card__body">
        <div class="premium-member-card__icon-wrap">
          <v-icon color="#FFD54F" size="32">mdi-crown</v-icon>
        </div>
        <div class="premium-member-card__content">
          <div class="premium-member-card__header">
            <strong class="premium-member-card__title">Você é Associado Premium</strong>
            <premium-member-badge size="x-small" />
          </div>
          <p class="premium-member-card__text mb-0">
            Suas comissões de adesão na rede estão desbloqueadas. Continue expandindo sua rede!
          </p>
          <div v-if="voucherInfo?.hasPremiumVoucher" class="premium-member-card__voucher mt-2">
            <v-icon small color="deep-purple darken-2" class="mr-1">mdi-ticket-percent</v-icon>
            <span>{{ voucherInfo.message }}</span>
          </div>
          <div v-if="voucherInfo?.hasPartnerPremiumVoucher && voucherInfo.partnerMessage" class="premium-member-card__voucher mt-2">
            <v-icon small color="deep-purple darken-2" class="mr-1">mdi-store</v-icon>
            <span>{{ voucherInfo.partnerMessage }}</span>
          </div>
        </div>
        <v-btn
          text
          small
          color="deep-purple darken-2"
          class="premium-member-card__cta"
          @click="goToMyNetwork"
        >
          Minha Rede
          <v-icon right small>mdi-arrow-right</v-icon>
        </v-btn>
      </v-card-text>
    </v-card>

    <premium-upsell-banner
      v-else-if="showPremiumUpsellOnHome"
      class="mb-4"
      variant="inline"
      :potential-earnings="premiumPotentialEarnings"
      title="Sua rede pode gerar comissões de adesão Premium"
      subtitle="Acesse Minha Rede para ver o potencial e desbloquear ganhos em até 5 níveis."
      cta-label="Conhecer o Premium"
      :show-secondary="false"
      @cta="goToPremiumUpsell"
    />

    <v-card v-if="!hasNoFranchiseFilterProfile" outlined class="filter-card mb-2">
      <v-card-text class="filter-card__body">
        <v-row dense align="center">
          <v-col cols="12" md="6">
            <div class="field-label-with-help">
              Franquia
              <v-tooltip bottom max-width="280">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    class="help-icon"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                Filtra os dados para uma franquia específica.
              </v-tooltip>
            </div>
            <v-select
              :items="sessionFranchises"
              v-model="franchiseId"
              :item-text="displayTextFranchise"
              item-value="id"
              label="Selecione a franquia"
              dense
              filled
              hide-details
            ></v-select>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <div v-if="loading">
      <v-row dense>
        <v-col v-for="index in 4" :key="`skeleton-${index}`" cols="12" sm="6" lg="3">
          <v-skeleton-loader type="card" boilerplate class="kpi-skeleton" />
        </v-col>
      </v-row>
    </div>

    <div v-else>
      <v-row dense class="dashboard-page__kpis">
        <v-col cols="12" sm="6" lg="3">
          <v-hover v-slot="{ hover }">
            <v-card class="metric-card metric-card--finance" :class="{ 'metric-card--raised': hover }" elevation="0">
              <v-card-text class="metric-card__inner">
                <div class="metric-card__head">
                  <div class="metric-icon-wrap metric-icon-wrap--finance">
                    <v-icon color="#1f8a4d">mdi-cash-multiple</v-icon>
                  </div>
                  <div class="metric-title">
                    Faturamento
                    <v-tooltip bottom max-width="280">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small class="help-icon" v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      Valor total faturado no período selecionado.
                    </v-tooltip>
                  </div>
                </div>
                <div class="metric-value">{{ formatPrice(dashboard.invoicing) }}</div>
              </v-card-text>
            </v-card>
          </v-hover>
        </v-col>
        <v-col cols="12" sm="6" lg="3">
          <v-hover v-slot="{ hover }">
            <v-card class="metric-card metric-card--finance" :class="{ 'metric-card--raised': hover }" elevation="0">
              <v-card-text class="metric-card__inner">
                <div class="metric-card__head">
                  <div class="metric-icon-wrap metric-icon-wrap--finance">
                    <v-icon color="#1f8a4d">mdi-chart-line</v-icon>
                  </div>
                  <div class="metric-title">
                    Lucro
                    <v-tooltip bottom max-width="280">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small class="help-icon" v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      Diferença entre faturamento e custos no período selecionado.
                    </v-tooltip>
                  </div>
                </div>
                <div class="metric-value">{{ formatPrice(dashboard.profit) }}</div>
              </v-card-text>
            </v-card>
          </v-hover>
        </v-col>
        <v-col cols="12" sm="6" lg="3">
          <v-hover v-slot="{ hover }">
            <v-card class="metric-card metric-card--network" :class="{ 'metric-card--raised': hover }" elevation="0">
              <v-card-text class="metric-card__inner">
                <div class="metric-card__head">
                  <div class="metric-icon-wrap metric-icon-wrap--network">
                    <v-icon color="#242968">mdi-store</v-icon>
                  </div>
                  <div class="metric-title">
                    Parceiros
                    <v-tooltip bottom max-width="280">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small class="help-icon" v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      Quantidade total de parceiros ativos no contexto filtrado.
                    </v-tooltip>
                  </div>
                </div>
                <div class="metric-value">{{ dashboard.totalPartners }}</div>
              </v-card-text>
            </v-card>
          </v-hover>
        </v-col>
        <v-col cols="12" sm="6" lg="3">
          <v-hover v-slot="{ hover }">
            <v-card class="metric-card metric-card--network" :class="{ 'metric-card--raised': hover }" elevation="0">
              <v-card-text class="metric-card__inner">
                <div class="metric-card__head">
                  <div class="metric-icon-wrap metric-icon-wrap--network">
                    <v-icon color="#242968">mdi-account-group</v-icon>
                  </div>
                  <div class="metric-title">
                    Consumidores
                    <v-tooltip bottom max-width="280">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small class="help-icon" v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      Total de consumidores que geraram movimentação no período.
                    </v-tooltip>
                  </div>
                </div>
                <div class="metric-value">{{ dashboard.totalCustomers }}</div>
              </v-card-text>
            </v-card>
          </v-hover>
        </v-col>
      </v-row>

      <v-row dense class="dashboard-page__adhesion mt-2">
        <v-col cols="12" class="pb-0">
          <p class="dashboard-adhesion-heading">
            Adesões de parceiros (premium)
            <v-tooltip bottom max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <v-icon small class="help-icon" v-bind="attrs" v-on="on">
                  mdi-help-circle-outline
                </v-icon>
              </template>
              Faturamento e lucro referentes apenas a pagamentos de adesão premium de empresas parceiras no período.
            </v-tooltip>
          </p>
        </v-col>
        <v-col cols="12" sm="6">
          <v-hover v-slot="{ hover }">
            <v-card class="metric-card metric-card--finance" :class="{ 'metric-card--raised': hover }" elevation="0">
              <v-card-text class="metric-card__inner">
                <div class="metric-card__head">
                  <div class="metric-icon-wrap metric-icon-wrap--finance">
                    <v-icon color="#1f8a4d">mdi-handshake-outline</v-icon>
                  </div>
                  <div class="metric-title">
                    Faturamento (adesões)
                    <v-tooltip bottom max-width="280">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small class="help-icon" v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      Soma dos valores pagos em adesões premium de parceiros no período.
                    </v-tooltip>
                  </div>
                </div>
                <div class="metric-value">{{ formatPrice(partnerAdhesionInvoicing) }}</div>
              </v-card-text>
            </v-card>
          </v-hover>
        </v-col>
        <v-col cols="12" sm="6">
          <v-hover v-slot="{ hover }">
            <v-card class="metric-card metric-card--finance" :class="{ 'metric-card--raised': hover }" elevation="0">
              <v-card-text class="metric-card__inner">
                <div class="metric-card__head">
                  <div class="metric-icon-wrap metric-icon-wrap--finance">
                    <v-icon color="#1f8a4d">mdi-piggy-bank-outline</v-icon>
                  </div>
                  <div class="metric-title">
                    Lucro (adesões)
                    <v-tooltip bottom max-width="300">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small class="help-icon" v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      Lucro do contexto atual sobre as adesões premium no período.
                    </v-tooltip>
                  </div>
                </div>
                <div class="metric-value">{{ formatPrice(partnerAdhesionProfit) }}</div>
              </v-card-text>
            </v-card>
          </v-hover>
        </v-col>
      </v-row>

      <v-row
        v-if="showAssociateNetworkMetrics"
        dense
        class="dashboard-page__adhesion mt-2"
      >
        <v-col cols="12" class="pb-0">
          <p class="dashboard-adhesion-heading">
            Rede de associados
            <v-tooltip bottom max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <v-icon small class="help-icon" v-bind="attrs" v-on="on">
                  mdi-help-circle-outline
                </v-icon>
              </template>
              Comissões de adesão Premium recebidas da sua rede e potencial ainda não desbloqueado.
            </v-tooltip>
          </p>
        </v-col>
        <v-col cols="12" sm="6">
          <v-hover v-slot="{ hover }">
            <v-card
              class="metric-card metric-card--network"
              :class="{ 'metric-card--raised': hover }"
              elevation="0"
            >
              <v-card-text class="metric-card__inner">
                <div class="metric-card__head">
                  <div class="metric-icon-wrap metric-icon-wrap--network">
                    <v-icon color="#2dcc6b">mdi-cash-check</v-icon>
                  </div>
                  <div class="metric-title">
                    Recebido com associados
                    <v-tooltip bottom max-width="280">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small class="help-icon" v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      Total de comissões de adesão Premium já recebidas da sua rede de associados.
                    </v-tooltip>
                  </div>
                </div>
                <div
                  class="metric-value"
                  :class="{ 'metric-value--locked': associateEarningsLocked }"
                >
                  <v-icon v-if="associateEarningsLocked" x-small class="mr-1">mdi-lock</v-icon>
                  {{ associateReceivedLabel }}
                </div>
              </v-card-text>
            </v-card>
          </v-hover>
        </v-col>
        <v-col cols="12" sm="6">
          <v-hover v-slot="{ hover }">
            <v-card
              class="metric-card metric-card--network"
              :class="{ 'metric-card--raised': hover, 'metric-card--highlight': associateEarningsLocked && associateOpportunityEarnings > 0 }"
              elevation="0"
            >
              <v-card-text class="metric-card__inner">
                <div class="metric-card__head">
                  <div class="metric-icon-wrap metric-icon-wrap--network">
                    <v-icon color="#24305E">mdi-trending-up</v-icon>
                  </div>
                  <div class="metric-title">
                    Oportunidades
                    <v-tooltip bottom max-width="280">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small class="help-icon" v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      Potencial adicional de comissões com associados não Premium na sua rede.
                    </v-tooltip>
                  </div>
                </div>
                <div class="metric-value">{{ formatPrice(associateOpportunityEarnings) }}</div>
              </v-card-text>
            </v-card>
          </v-hover>
        </v-col>
      </v-row>

      <v-row dense class="dashboard-page__insights mt-1">
        <v-col cols="12" md="8" class="dashboard-page__insights-col">
          <v-card outlined class="insight-card insight-card--dense dashboard-page__insights-card">
            <v-card-title class="insight-title insight-title--dense">
              Resumo de desempenho
              <v-tooltip bottom max-width="280">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small class="help-icon" v-bind="attrs" v-on="on">
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                Indicadores derivados para análise de eficiência e qualidade da base.
              </v-tooltip>
            </v-card-title>
            <v-card-text class="insight-card__body">
              <v-row class="insight-grid" dense>
                <v-col cols="12" md="6">
                  <div class="insight-item">
                    <span class="insight-label with-help">
                      Margem de lucro
                      <v-tooltip bottom max-width="260">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon x-small class="help-icon" v-bind="attrs" v-on="on">
                            mdi-help-circle-outline
                          </v-icon>
                        </template>
                        Percentual de lucro sobre o faturamento.
                      </v-tooltip>
                    </span>
                    <span class="insight-value">{{ profitMarginLabel }}</span>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="insight-item">
                    <span class="insight-label with-help">
                      Ticket médio por consumidor
                      <v-tooltip bottom max-width="260">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon x-small class="help-icon" v-bind="attrs" v-on="on">
                            mdi-help-circle-outline
                          </v-icon>
                        </template>
                        Valor médio faturado por consumidor no período.
                      </v-tooltip>
                    </span>
                    <span class="insight-value">{{ averageRevenuePerCustomerLabel }}</span>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="insight-item">
                    <span class="insight-label with-help">
                      Clientes por parceiro
                      <v-tooltip bottom max-width="260">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon x-small class="help-icon" v-bind="attrs" v-on="on">
                            mdi-help-circle-outline
                          </v-icon>
                        </template>
                        Média de consumidores atendidos por parceiro.
                      </v-tooltip>
                    </span>
                    <span class="insight-value">{{ customersPerPartnerLabel }}</span>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="insight-item">
                    <span class="insight-label with-help">
                      Parceiros premium
                      <v-tooltip bottom max-width="260">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon x-small class="help-icon" v-bind="attrs" v-on="on">
                            mdi-help-circle-outline
                          </v-icon>
                        </template>
                        Quantidade e participação de parceiros premium na base total.
                      </v-tooltip>
                    </span>
                    <span class="insight-value">
                      {{ dashboard.totalPremiumPartners || 0 }} ({{ premiumPartnerShareLabel }})
                    </span>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4" class="dashboard-page__insights-col">
          <v-card outlined class="insight-card insight-card--dense dashboard-page__insights-card">
            <v-card-title class="insight-title insight-title--dense">
              Leitura rápida
              <v-tooltip bottom max-width="280">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small class="help-icon" v-bind="attrs" v-on="on">
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                Resumo em chips para identificar rapidamente o cenário atual.
              </v-tooltip>
            </v-card-title>
            <v-card-text class="insight-card__body insight-card__body--chips">
              <div class="quick-read-grid">
                <div class="quick-read-cell">
                  <v-tooltip bottom max-width="260" class="quick-read-tooltip">
                    <template v-slot:activator="{ on, attrs }">
                      <div class="quick-read-activator" v-bind="attrs" v-on="on">
                        <v-chip class="quick-read-chip ma-0" color="#F2FFF7" text-color="#1F8A4D">
                          <span class="quick-read-chip__text">Margem: {{ profitMarginLabel }}</span>
                        </v-chip>
                      </div>
                    </template>
                    Percentual de lucro em relação ao faturamento.
                  </v-tooltip>
                </div>
                <div class="quick-read-cell">
                  <v-tooltip bottom max-width="260" class="quick-read-tooltip">
                    <template v-slot:activator="{ on, attrs }">
                      <div class="quick-read-activator" v-bind="attrs" v-on="on">
                        <v-chip class="quick-read-chip ma-0" color="#F1F4FF" text-color="#2F3E8A">
                          <span class="quick-read-chip__text">Premium: {{ premiumPartnerShareLabel }}</span>
                        </v-chip>
                      </div>
                    </template>
                    Participação de parceiros premium sobre o total.
                  </v-tooltip>
                </div>
                <div class="quick-read-cell">
                  <v-tooltip bottom max-width="260" class="quick-read-tooltip">
                    <template v-slot:activator="{ on, attrs }">
                      <div class="quick-read-activator" v-bind="attrs" v-on="on">
                        <v-chip class="quick-read-chip ma-0" color="#FFF6ED" text-color="#C25A00">
                          <span class="quick-read-chip__text">Ticket: {{ averageRevenuePerCustomerLabel }}</span>
                        </v-chip>
                      </div>
                    </template>
                    Faturamento médio por consumidor no período.
                  </v-tooltip>
                </div>
                <div class="quick-read-cell">
                  <v-tooltip bottom max-width="260" class="quick-read-tooltip">
                    <template v-slot:activator="{ on, attrs }">
                      <div class="quick-read-activator" v-bind="attrs" v-on="on">
                        <v-chip class="quick-read-chip ma-0" color="#F5F5F5" text-color="#424242">
                          <span class="quick-read-chip__text">Associados: {{ dashboard.totalAssociates || 0 }}</span>
                        </v-chip>
                      </div>
                    </template>
                    Quantidade total de associados vinculados ao contexto atual.
                  </v-tooltip>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-if="isAssociateUser" dense class="mt-1">
        <v-col cols="12">
          <associate-career-panel :dashboard="dashboard" />
        </v-col>
      </v-row>

    </div>
    </template>

    <voucher-benefit-modal
      v-model="voucherNoticeOpen"
      :quantity="voucherNoticeQuantity"
      :target="voucherNoticeTarget"
      @dismiss="handleDismissVoucherNotice"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import TitleVue from "@/components/title/Title.vue";
import PageLoading from "@/components/loading/PageLoading.vue";
import AssociateCareerPanel from "@/components/career/AssociateCareerPanel.vue";
import PremiumMemberBadge from "@/components/premium/PremiumMemberBadge.vue";
import PremiumUpsellBanner from "@/components/premium/PremiumUpsellBanner.vue";
import VoucherBenefitModal from "@/components/premium/VoucherBenefitModal.vue";
import { GetPremiumVouchers } from "@/services/premium/GetPremiumVouchers";
import { PremiumVoucherInfo } from "@/types/premium/PremiumVoucher";
import { DismissPremiumVoucherNotice } from "@/services/premium/DismissPremiumVoucherNotice";
import { GetDashboard } from "@/services/dashboard/GetDashboard";
import { GetPremiumDashboard } from "@/services/premium/GetPremiumDashboard";
import { DashboardType } from "@/types/dashboard/Dashboard";
import { SessionFranchiseType } from "@/types/user/SessionInfo";

type HomeViewDataType = {
  dialog: boolean;
  selectedRange: string[];
  startDate: string;
  endDate: string;
  mobileBreakpoint: number;
  loading: boolean;
  inlineRefreshing: boolean;
  hasLoadedOnce: boolean;
  lastUpdatedAt: Date | null;
  franchiseId: string;
  dashboard: DashboardType;
  associateReceivedEarnings: number;
  associateOpportunityEarnings: number;
  associateEarningsLocked: boolean;
  voucherNoticeOpen: boolean;
  voucherNoticeQuantity: number;
  voucherNoticeTarget: "associate" | "partner";
  voucherInfo: PremiumVoucherInfo | null;
};

export default defineComponent({
  components: {
    TitleVue,
    PageLoading,
    AssociateCareerPanel,
    PremiumMemberBadge,
    PremiumUpsellBanner,
    VoucherBenefitModal,
  },
  data(): HomeViewDataType {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    const formattedToday = today.toISOString().split("T")[0];
    const formattedThirtyDaysAgo = thirtyDaysAgo.toISOString().split("T")[0];
    return {
      dialog: false,
      selectedRange: [formattedThirtyDaysAgo, formattedToday],
      startDate: formattedThirtyDaysAgo,
      endDate: formattedToday,
      mobileBreakpoint: 600,
      loading: true,
      inlineRefreshing: false,
      hasLoadedOnce: false,
      lastUpdatedAt: null,
      franchiseId: "",
      dashboard: {
        invoicing: 0,
        profit: 0,
        totalCustomers: 0,
        totalPartners: 0,
        totalAssociates: 0,
        totalPremiumPartners: 0,
        totalPremiumAssociates: 0,
        partnerAdhesionInvoicing: 0,
        partnerAdhesionProfit: 0,
      } as DashboardType,
      associateReceivedEarnings: 0,
      associateOpportunityEarnings: 0,
      associateEarningsLocked: false,
      voucherNoticeOpen: false,
      voucherNoticeQuantity: 0,
      voucherNoticeTarget: "associate" as "associate" | "partner",
      voucherInfo: null,
    };
  },
  mounted() {
    this.loadDashboardOnEnter();
    void this.loadVoucherNotice();
    void this.loadVoucherInfo();
  },
  computed: {
    isCollaboratorManagerUser(): boolean {
      return this.$store.getters.isCollaboratorManager;
    },
    effectiveAssociateId(): string {
      if (this.isCollaboratorManagerUser) return "";
      return this.$store.state.session?.associateId ?? "";
    },
    hasNoFranchiseFilterProfile(): boolean {
      return this.$store.getters.hasNoFranchiseFilterProfile;
    },
    isAssociateUser(): boolean {
      const hasAssociateProfile = this.$store.getters.isAssociateProfile;
      const selectedFranchise = this.sessionFranchises.find(
        (franchise) => franchise.id === this.franchiseId,
      );
      const isFranchiseAdmin = selectedFranchise?.isAdmin ?? false;
      return hasAssociateProfile && !isFranchiseAdmin;
    },
    isAssociatePremiumActive(): boolean {
      return this.$store.getters.isAssociatePremiumActive;
    },
    showPremiumUpsellOnHome(): boolean {
      return this.$store.getters.shouldShowPremiumUpsell;
    },
    showAssociateNetworkMetrics(): boolean {
      return (
        this.$store.getters.isAssociateProfile || this.$store.getters.isFranchiseAdmin
      );
    },
    premiumPotentialEarnings(): number {
      return this.associateOpportunityEarnings;
    },
    associateReceivedLabel(): string {
      if (this.associateEarningsLocked) return "R$ 0,00";
      return this.formatPrice(this.associateReceivedEarnings);
    },
    sessionFranchises(): SessionFranchiseType[] {
      return this.$store.getters.sessionFranchises;
    },
    handleFormatDate(): string[] {
      const datesString = this.selectedRange?.map((dates: string | undefined) => {
        if (dates) {
          const [year, month, day] = dates.split("-");
          return ` ${day}/${month}/${year}`;
        }
        return "";
      });
      return datesString || [];
    },
    profitMarginLabel(): string {
      if (!this.dashboard.invoicing) return "0%";
      const margin = (this.dashboard.profit / this.dashboard.invoicing) * 100;
      return `${margin.toFixed(1).replace(".", ",")}%`;
    },
    averageRevenuePerCustomerLabel(): string {
      if (!this.dashboard.totalCustomers) return "R$ 0,00";
      return this.formatPrice(this.dashboard.invoicing / this.dashboard.totalCustomers);
    },
    customersPerPartnerLabel(): string {
      if (!this.dashboard.totalPartners) return "0";
      return (this.dashboard.totalCustomers / this.dashboard.totalPartners).toFixed(1).replace(".", ",");
    },
    premiumPartnerShareLabel(): string {
      if (!this.dashboard.totalPartners) return "0%";
      const premiumPartners = this.dashboard.totalPremiumPartners || 0;
      const percentage = (premiumPartners / this.dashboard.totalPartners) * 100;
      return `${percentage.toFixed(1).replace(".", ",")}%`;
    },
    partnerAdhesionInvoicing(): number {
      return this.dashboard.partnerAdhesionInvoicing ?? 0;
    },
    partnerAdhesionProfit(): number {
      return this.dashboard.partnerAdhesionProfit ?? 0;
    },
    greetingFirstName(): string {
      const raw = (this.$store.state.session?.name as string | undefined)?.trim();
      if (!raw) return "";
      return raw.split(/\s+/)[0] ?? "";
    },
    welcomeSubtitle(): string {
      if (this.greetingFirstName) {
        return `Olá, ${this.greetingFirstName} — acompanhe o desempenho da operação no período escolhido.`;
      }
      return "Acompanhe o desempenho da operação no período escolhido.";
    },
    selectedFranchiseLabel(): string {
      if (this.hasNoFranchiseFilterProfile) return "";
      const franchise = this.sessionFranchises.find((f) => f.id === this.franchiseId);
      if (!franchise) return "";
      return this.displayTextFranchise(franchise);
    },
    lastUpdatedLabel(): string {
      if (!this.lastUpdatedAt) return "—";
      return new Intl.DateTimeFormat("pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
      }).format(this.lastUpdatedAt);
    },
  },
  watch: {
    franchiseId(newValue: string) {
      if (this.hasNoFranchiseFilterProfile) return;
      if (newValue) {
        this.handleLoadDashboard();
      }
    },
    sessionFranchises: {
      handler(franchises: SessionFranchiseType[]) {
        if (franchises.length > 0 && !this.franchiseId) {
          this.franchiseId = franchises[0].id;
          if (this.hasNoFranchiseFilterProfile) {
            this.handleLoadDashboard();
          }
          return;
        }
        if (franchises.length === 0 && !this.hasNoFranchiseFilterProfile) {
          this.loading = false;
        }
      },
      immediate: true,
    },
  },
  methods: {
    loadDashboardOnEnter() {
      if (this.hasNoFranchiseFilterProfile) {
        void this.handleLoadDashboard();
        return;
      }

      if (this.franchiseId) {
        void this.handleLoadDashboard();
      }
    },
    resolvePremiumFranchiseId(): string {
      if (!this.hasNoFranchiseFilterProfile && this.franchiseId) {
        return this.franchiseId;
      }
      return (
        this.$store.state.session?.associateFranchiseId ||
        this.sessionFranchises[0]?.id ||
        ""
      );
    },
    async loadAssociateNetworkMetrics() {
      if (!this.showAssociateNetworkMetrics) {
        this.associateReceivedEarnings = 0;
        this.associateOpportunityEarnings = 0;
        this.associateEarningsLocked = false;
        return;
      }

      const franchiseId = this.resolvePremiumFranchiseId();
      if (!franchiseId) return;

      try {
        const premiumDashboard = await new GetPremiumDashboard().execute(franchiseId);
        const canSubscribe =
          premiumDashboard.canSubscribePremium !== false &&
          premiumDashboard.status !== "ACTIVE";
        this.associateEarningsLocked = canSubscribe;
        this.associateReceivedEarnings = premiumDashboard.totalReceivedFromNetwork ?? 0;
        this.associateOpportunityEarnings = premiumDashboard.totalPotentialEarnings ?? 0;
      } catch {
        this.associateReceivedEarnings = 0;
        this.associateOpportunityEarnings = 0;
        this.associateEarningsLocked = false;
      }
    },
    goToPremiumUpsell() {
      this.$router.push({ name: "associates", query: { adesao: "premium" } });
    },
    async loadVoucherInfo() {
      if (!this.$store.getters.canUsePremiumVouchers) {
        this.voucherInfo = null;
        return;
      }
      try {
        this.voucherInfo = await new GetPremiumVouchers().execute();
      } catch {
        this.voucherInfo = null;
      }
    },
    async loadVoucherNotice() {
      if (!this.$store.getters.canUsePremiumVouchers) return;
      try {
        const info = await new GetPremiumVouchers().execute();
        this.voucherInfo = info;
        if ((info.partnerNoticePending ?? 0) > 0) {
          this.voucherNoticeQuantity = info.partnerNoticePending;
          this.voucherNoticeTarget = "partner";
          this.voucherNoticeOpen = true;
        } else if ((info.noticePending ?? 0) > 0) {
          this.voucherNoticeQuantity = info.noticePending;
          this.voucherNoticeTarget = "associate";
          this.voucherNoticeOpen = true;
        }
      } catch {
        // ignore when vouchers are unavailable
      }
    },
    async handleDismissVoucherNotice() {
      try {
        await new DismissPremiumVoucherNotice().execute();
        await this.$store.dispatch("refreshSession");
        await Promise.all([
          this.loadVoucherInfo(),
          this.handleLoadDashboard({ silent: true }),
        ]);
        this.$store.dispatch("showSnack", {
          message: "Parabéns! Seu Premium e seus vouchers já estão ativos no painel.",
          color: "success",
          timer: 5000,
        });
      } catch {
        this.$store.dispatch("showSnack", {
          message: "Não foi possível atualizar seus dados. Recarregue a página.",
          color: "error",
        });
      }
    },
    goToMyNetwork() {
      this.$router.push({ name: "associates" });
    },
    closeDialog() {
      this.dialog = false;
    },
    refreshDashboard() {
      void this.handleLoadDashboard({ silent: true });
    },
    async handleLoadDashboard(options?: { silent?: boolean }) {
      const silent = options?.silent === true;
      if (silent) {
        this.inlineRefreshing = true;
      } else {
        this.loading = true;
      }
      const getDashboard = new GetDashboard();
      if (this.selectedRange.length > 0) {
        if (this.selectedRange[0] > this.selectedRange[1]) {
          this.startDate = this.selectedRange[1];
          this.endDate = this.selectedRange[0];
        } else {
          this.startDate = this.selectedRange[0];
          this.endDate = this.selectedRange[1];
        }
      }
      try {
        const dashboard = await getDashboard.execute(
          this.startDate,
          this.endDate,
          this.hasNoFranchiseFilterProfile ? undefined : this.franchiseId,
          this.hasNoFranchiseFilterProfile ? this.effectiveAssociateId : undefined,
        );
        this.dashboard = dashboard;
        this.lastUpdatedAt = new Date();
        this.hasLoadedOnce = true;
        await this.loadAssociateNetworkMetrics();
      } finally {
        this.loading = false;
        this.inlineRefreshing = false;
        this.closeDialog();
      }
    },
    handleNewFranchise() {
      this.$router.push({
        name: "franchiseForm",
        params: { modeForm: "new" },
      });
    },
    formatPrice(value: number): string {
      if (!value && value !== 0) return "";
      const formattedValue = value.toFixed(2).replace(".", ",");
      return `R$ ${formattedValue}`;
    },
    displayTextFranchise(item: SessionFranchiseType): string {
      if (item.cityName && item.state) {
        return `${item.cityName} - ${item.state}`;
      }
      return item.name ?? item.id;
    },
  },
});
</script>

<style scoped lang="scss">
.dashboard-adhesion-heading {
  margin: 0 0 4px;
  font-size: 13px;
  font-weight: 700;
  color: #242968;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.dashboard-page {
  color: #1f243f;
}

.dashboard-page--compact {
  padding-bottom: 2px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
}

.header--compact ::v-deep h3 {
  font-size: 1.2rem !important;
  line-height: 1.25;
  margin: 0 !important;
}

.header__titles {
  max-width: 720px;
}

.header__welcome-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.premium-member-card {
  border-color: rgba(255, 193, 7, 0.45) !important;
  background: linear-gradient(135deg, #fffdf5 0%, #fff9e6 55%, #ffffff 100%);
}

.premium-member-card__body {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 18px !important;
}

.premium-member-card__icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: linear-gradient(135deg, #1a1a2e 0%, #4527a0 100%);
}

.premium-member-card__content {
  flex: 1;
  min-width: 0;
}

.premium-member-card__header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 4px;
}

.premium-member-card__title {
  font-size: 1rem;
  color: #1a1a2e;
}

.premium-member-card__text {
  font-size: 0.85rem;
  color: #555;
  line-height: 1.45;
}

.premium-member-card__voucher {
  display: flex;
  align-items: center;
  font-size: 0.82rem;
  color: #4527a0;
  font-weight: 600;
}

.premium-member-card__cta {
  flex-shrink: 0;
}

.header__actions {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px 12px;
}

.header-period {
  min-width: 260px;
  max-width: 320px;
}

.header__status {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.header-period__field {
  min-width: 260px;
}

.dashboard-subtitle {
  margin: 2px 0 0;
  font-size: 13px;
  line-height: 1.35;
  color: #5c6485;
  font-weight: 500;
}

.dashboard-context {
  display: inline-flex;
  align-items: center;
  margin: 4px 0 0;
  font-size: 12px;
  font-weight: 600;
  color: #242968;
}

.dashboard-context__icon {
  margin-right: 4px;
  color: #242968 !important;
}

.update-chip {
  border-radius: 10px !important;
  height: 28px !important;
}

.filter-card {
  border-radius: 12px;
  border-color: rgba(36, 41, 104, 0.12) !important;
  background: linear-gradient(145deg, #ffffff, #f7f9ff);
}

.filter-card__body {
  padding: 10px 14px 12px !important;
}

.field-label-with-help {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 2px;
  font-size: 12px;
  font-weight: 600;
  color: #4f5675;
}

.kpi-skeleton {
  border-radius: 12px;
  min-height: 96px;
}

.dashboard-page--compact ::v-deep .filter-card .v-input .v-input__slot {
  min-height: 36px !important;
}

.dashboard-page--compact ::v-deep .filter-card .v-text-field input {
  padding-top: 6px !important;
  padding-bottom: 6px !important;
}

.dashboard-page--compact ::v-deep .header-period__field .v-input__slot {
  min-height: 32px !important;
}

.dashboard-page--compact ::v-deep .header-period__field input {
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}

@media (max-width: 960px) {
  .header {
    flex-direction: column;
    align-items: stretch;
  }

  .header__titles {
    max-width: 100%;
  }

  .header__actions {
    justify-content: flex-start;
    align-items: stretch;
  }

  .header-period {
    width: 100%;
    max-width: 100%;
  }

  .header-period__field {
    min-width: 0;
  }
}

tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.05);
}

.metric-card {
  border-radius: 12px;
  border: 1px solid rgba(36, 41, 104, 0.08);
  box-shadow: 0 4px 14px rgba(23, 29, 52, 0.07);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.metric-card--raised {
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(23, 29, 52, 0.1) !important;
}

.metric-card__inner {
  padding: 12px 14px !important;
}

.metric-card__head {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 6px;
}

.metric-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.metric-icon-wrap .v-icon {
  font-size: 20px !important;
}

.metric-icon-wrap--finance {
  background: linear-gradient(145deg, rgba(36, 168, 89, 0.18), rgba(36, 168, 89, 0.08));
}

.metric-icon-wrap--network {
  background: linear-gradient(145deg, rgba(36, 41, 104, 0.14), rgba(36, 41, 104, 0.06));
}

.metric-card--finance {
  border-top: 3px solid #24a859;
}

.metric-card--network {
  border-top: 3px solid #242968;
}

.metric-card--highlight {
  border-top-color: #e5183d;
}

.metric-value--locked {
  color: #9e9e9e;
}

.metric-title {
  font-size: 12px;
  color: #5a607d;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  padding-top: 1px;
  line-height: 1.3;
}

.metric-value {
  font-size: 22px;
  line-height: 1.1;
  font-weight: 700;
  color: #222744;
}

/* Resumo de desempenho + Leitura rápida: mesma altura no desktop */
.dashboard-page__insights {
  align-items: stretch !important;
}

.dashboard-page__insights-col {
  display: flex;
  flex-direction: column;
}

.dashboard-page__insights-card {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
}

.dashboard-page__insights-card ::v-deep .v-card__text.insight-card__body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.insight-card {
  border-radius: 12px;
  border-color: rgba(36, 41, 104, 0.12) !important;
}

.insight-card--dense ::v-deep .v-card__title {
  padding: 10px 14px 6px !important;
}

.insight-title--dense {
  font-size: 14px !important;
  line-height: 1.3 !important;
}

.insight-card__body {
  padding: 8px 14px 12px !important;
}

.insight-card__body--chips {
  padding: 10px 12px 12px !important;
}

.quick-read-grid {
  flex: 1 1 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 10px;
  min-height: 0;
  align-content: stretch;
}

.quick-read-cell {
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
}

.quick-read-cell ::v-deep .quick-read-tooltip {
  flex: 1 1 auto;
  display: flex !important;
  flex-direction: column;
  width: 100%;
  min-height: 0;
}

.quick-read-activator {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  width: 100%;
  cursor: help;
  border-radius: 10px;
}

.quick-read-chip {
  flex: 1 1 auto;
  width: 100% !important;
  height: 100% !important;
  min-height: 64px;
  justify-content: center !important;
  align-items: center !important;
  border-radius: 10px !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  line-height: 1.25 !important;
  white-space: normal !important;
  text-align: center;
  padding: 10px 8px !important;
}

.quick-read-chip__text {
  display: block;
  max-width: 100%;
  word-break: break-word;
}

.insight-grid .insight-item {
  padding: 8px 10px;
  border-radius: 8px;
  background: rgba(247, 249, 255, 0.85);
  border: 1px solid rgba(36, 41, 104, 0.06);
  min-height: 54px;
}

.insight-title {
  font-size: 16px;
  font-weight: 700;
  color: #222744;
}

.insight-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.insight-label {
  color: #646b88;
  font-size: 11px;
}

.with-help {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.insight-value {
  color: #1f243f;
  font-size: 16px;
  font-weight: 700;
}

.help-icon {
  color: #7d86a8 !important;
  cursor: help;
}

.dashboard-page ::v-deep .v-tooltip__content {
  background: #1f243f !important;
  color: #ffffff !important;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.28);
  opacity: 1 !important;
}

</style>
